import type { IStringTool } from "../core/IStringTool.ts"
import TransformNullifyTool from "./TransformNullifyTool.ts"

/**
 * Input for calculating the position of an element relative to a container.
 */
interface RelativePositionInput {
  /** The DOM element whose position should be calculated. */
  element: HTMLElement

  /** Optional container to measure against. Defaults to `document.body`. */
  container?: HTMLElement
}

/**
 * Output: relative position in pixels.
 */
interface RelativePositionOutput {
  /** Distance from the top of the container. */
  top: number

  /** Distance from the left of the container. */
  left: number
}

/**
 * Calculates an element's position relative to a container.
 * Uses `TransformNullifyTool` to account for CSS transforms.
 */
export default class RelativePositionTool
  implements IStringTool<RelativePositionInput, RelativePositionOutput>
{
  constructor(
    /** Optional tool for CSS transform-neutral measurements. */
    private transformTool = new TransformNullifyTool()
  ) {}

  /**
   * @returns Relative top/left position of element within container.
   */
  process({ element, container = document.body }: RelativePositionInput): RelativePositionOutput {
    let containerRect: DOMRect
    try {
      containerRect = container.getBoundingClientRect()
    } catch {
      containerRect = document.body.getBoundingClientRect()
    }

    const elRect = this.transformTool.process({ element })

    return {
      top: elRect.top - containerRect.top,
      left: elRect.left - containerRect.left,
    }
  }
}
